/**
 * One event's tile in {@link CaptureTimeline} — the horizontal scrolling
 * strip's fixed-width mini-tile, or (vertical mode) a compact list row.
 * Owns its own derived model (labels, tooltip, action eligibility, click
 * handlers) but leaves the shared popup-menu state to the parent: it calls
 * `onOpenMenu` and the parent renders the single `<Menu>`.
 */
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DevicesIcon from '@mui/icons-material/Devices';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DownloadIcon from '@mui/icons-material/Download';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { SessionPipelineDot } from '../SessionPipelineStrip';

import {
  formatTimelineLabel,
  formatTooltipDetail,
  hasAnyAction,
  QUALITY_CHIP,
  type TimelineEvent,
} from './helpers';

/** Where to anchor the shared popup — an element (left-click) or a cursor
 *  position (right-click). */
export type MenuAnchor = {
  anchor?: HTMLElement;
  position?: { top: number; left: number };
};

interface TimelineTileProps {
  event: TimelineEvent;
  displayTimeZone?: string;
  busyId?: string | null;
  selectedId?: string | null;
  vertical: boolean;
  hideSessionDownload: boolean;
  useActionMenu: boolean;
  contextMenu: boolean;
  secondaryLabel?: 'device' | 'participant';
  onDownload?: (e: TimelineEvent) => void;
  onAttach?: (e: TimelineEvent) => void;
  onTag?: (e: TimelineEvent) => void;
  onSelect?: (e: TimelineEvent) => void;
  onDisable?: (e: TimelineEvent) => void;
  onRestore?: (e: TimelineEvent) => void;
  onReplay?: (e: TimelineEvent) => void;
  onSplit?: (e: TimelineEvent) => void;
  onViewTrace?: (e: TimelineEvent) => void;
  /** Open the shared popup menu for this event, anchored per `where`. */
  onOpenMenu: (event: TimelineEvent, where: MenuAnchor) => void;
}

export function TimelineTile({
  event,
  displayTimeZone,
  busyId,
  selectedId,
  vertical,
  hideSessionDownload,
  useActionMenu,
  contextMenu,
  secondaryLabel,
  onDownload,
  onAttach,
  onTag,
  onSelect,
  onDisable,
  onRestore,
  onReplay,
  onSplit,
  onViewTrace,
  onOpenMenu,
}: TimelineTileProps) {
  const disabled = event.kind === 'session' && event.s3Key === null;
  const isSession = event.kind === 'session';
  const isDeleted = event.deletedAt !== null;
  // A split child leads with its operator-given cut name ("Closing");
  // the procedure name stays in the tile tooltip.
  const topLabel = isSession
    ? event.label || event.procedureName || 'Session'
    : 'Baseline';
  const { date, time } = formatTimelineLabel(event.capturedAt, displayTimeZone);
  // Cross-dimension line: name the axis the caller did NOT group on
  // (device in the by-participant view, participant in the by-device
  // view) so a bucketed tile still carries its other-half context.
  const secondaryText =
    secondaryLabel === 'device'
      ? event.deviceLabel
      : secondaryLabel === 'participant'
        ? event.participantLabel
        : null;
  // ROLES-AND-GO: the role taken in this recording, under the procedure.
  const roleLine =
    isSession && event.roleName ? (
      <Typography
        noWrap
        variant="caption"
        sx={{
          display: 'block',
          fontSize: '0.68rem',
          fontWeight: 650,
          color: 'primary.main',
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        {event.roleName}
      </Typography>
    ) : null;
  const secondaryLine = secondaryText ? (
    <Stack
      direction="row"
      spacing={0.4}
      alignItems="center"
      justifyContent={vertical ? 'flex-start' : 'center'}
      sx={{ minWidth: 0, maxWidth: '100%', mb: vertical ? 0 : 0.5 }}
    >
      {secondaryLabel === 'device' ? (
        <DevicesIcon
          sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }}
        />
      ) : (
        <PersonOutlineIcon
          sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }}
        />
      )}
      <Typography
        noWrap
        variant="caption"
        sx={{ fontSize: '0.68rem', color: 'text.secondary', minWidth: 0 }}
      >
        {secondaryText}
      </Typography>
    </Stack>
  ) : null;
  const tooltip =
    formatTooltipDetail(event, displayTimeZone) +
    (disabled ? '\n(no recording on file — kiosk skipped Export)' : '') +
    (isDeleted ? '\n(disabled — excluded from lists + scores)' : '');
  const selectable = onSelect !== undefined;
  const isSelected = selectable && selectedId === event.id;
  // Does this event have any action to offer in the popup menu?
  const anyAction = hasAnyAction(event, {
    onDownload,
    onAttach,
    onTag,
    onDisable,
    onRestore,
    onReplay,
    onSplit,
    onViewTrace,
    hideSessionDownload,
  });
  const clickOpensMenu = useActionMenu && anyAction;
  const rightClickMenu = contextMenu && anyAction;
  const clickable = selectable || clickOpensMenu;
  const onTileClick = (e: React.MouseEvent<HTMLElement>) => {
    if (clickOpensMenu) {
      onOpenMenu(event, { anchor: e.currentTarget });
    } else if (selectable) {
      onSelect(event);
    }
  };
  const onTileContextMenu = rightClickMenu
    ? (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        onOpenMenu(event, { position: { top: e.clientY, left: e.clientX } });
      }
    : undefined;
  return (
    <Tooltip title={tooltip}>
      <Stack
        alignItems="center"
        onClick={clickable ? onTileClick : undefined}
        onContextMenu={onTileContextMenu}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onTileClick(e as unknown as React.MouseEvent<HTMLElement>);
                }
              }
            : undefined
        }
        sx={{
          // Horizontal: a fixed-width tile in the scrolling strip.
          // Vertical (session-picker): a full-width list row.
          minWidth: vertical ? 0 : 96,
          maxWidth: vertical ? 'none' : 120,
          width: vertical ? '100%' : undefined,
          opacity: disabled ? 0.45 : isDeleted ? 0.55 : 1,
          cursor: clickable ? 'pointer' : 'default',
          transition: 'border-color 0.15s, background-color 0.15s',
          ...(vertical
            ? {
                // List-row: tinted pill + inset accent bar when active,
                // no boxed border.
                p: 1,
                borderRadius: 1.5,
                bgcolor: isSelected ? 'action.selected' : 'transparent',
                boxShadow: isSelected
                  ? (theme) => `inset 3px 0 0 ${theme.palette.primary.main}`
                  : 'none',
              }
            : {
                p: 0.5,
                borderRadius: 1,
                border: '2px solid',
                borderColor: isSelected ? 'primary.main' : 'transparent',
                bgcolor: isSelected ? 'action.selected' : 'transparent',
              }),
          '&:hover': clickable
            ? { bgcolor: isSelected ? 'action.selected' : 'action.hover' }
            : undefined,
        }}
      >
        {vertical ? (
          /* Compact list row: status dot · name + date·time · badge. */
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
              textAlign: 'left',
            }}
          >
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: isDeleted
                  ? 'text.disabled'
                  : isSession
                    ? 'primary.dark'
                    : 'secondary.dark',
              }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                noWrap
                sx={{
                  fontWeight: 750,
                  fontSize: '0.8rem',
                  lineHeight: 1.25,
                  color: isSession ? 'text.primary' : 'primary.dark',
                }}
              >
                {topLabel}
              </Typography>
              {roleLine}
              <Typography
                noWrap
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.68rem' }}
              >
                {date} · {time}
              </Typography>
              {secondaryLine}
            </Box>
            {/* Still on its way in, or needs attention (SESSION-PIPELINE). */}
            {isSession && event.pipeline && (
              <SessionPipelineDot pipeline={event.pipeline} />
            )}
            {isDeleted ? (
              <Chip
                label="Off"
                color="error"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  '& .MuiChip-label': { px: 0.6 },
                }}
              />
            ) : isSession && event.captureQuality ? (
              <Chip
                label={QUALITY_CHIP[event.captureQuality.status].label}
                color={QUALITY_CHIP[event.captureQuality.status].color}
                size="small"
                variant="outlined"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  '& .MuiChip-label': { px: 0.6 },
                }}
              />
            ) : null}
          </Box>
        ) : (
          <>
            {/* Procedure label above the icon (or "Baseline" for
                baselines). Two-line clamp so longer procedure
                names like "Video Laryngoscopy" wrap instead of
                overflowing; ellipsis on the third+ line keeps the
                tile height bounded. Tooltip carries the full text
                for anything that gets clipped. */}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                fontSize: '0.8rem',
                textAlign: 'center',
                lineHeight: 1.2,
                mb: 0.5,
                // Reserve two lines worth of height so all tiles
                // align horizontally regardless of label length.
                minHeight: 30,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                // Word-break so a single long word still wraps
                // rather than overflowing the column.
                wordBreak: 'break-word',
                // Ink for sessions — the strongest read, matching the
                // dashboard scorecard; the coloured .dark teal/olive read
                // washed-out. Baselines keep the teal so they still stand
                // apart (their "Baseline" text already differs too).
                color: isSession ? 'text.primary' : 'primary.dark',
              }}
            >
              {topLabel}
            </Typography>
            {roleLine}
            {/* Explicit "Disabled" marker on soft-deleted captures — the
                dimmed opacity alone reads as ambiguous, so label it. */}
            {isDeleted && (
              <Chip
                label="Disabled"
                color="error"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  mb: 0.5,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
            {/* Date over time, directly under the title. The date is
                the dominant token (caption-weight bold); time sits
                underneath in muted. */}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                }}
              >
                {date}
              </Typography>
              {/* Still on its way in, or needs attention (SESSION-PIPELINE). */}
              {isSession && event.pipeline && (
                <SessionPipelineDot pipeline={event.pipeline} />
              )}
            </Stack>
            <Typography
              variant="caption"
              sx={{
                whiteSpace: 'nowrap',
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'text.secondary',
                mb: 0.5,
              }}
            >
              {time}
            </Typography>
            {secondaryLine}
            {/* Capture-quality badge (ROADMAP CAPTURE-RATE-MONITOR) —
                green/amber/red by achieved sampling rate. Per-sensor
                device + Hz breakdown rides the tile tooltip above.
                Sessions only; hidden on rows with no quality data. */}
            {isSession && event.captureQuality && (
              <Chip
                label={QUALITY_CHIP[event.captureQuality.status].label}
                color={QUALITY_CHIP[event.captureQuality.status].color}
                size="small"
                variant="outlined"
                sx={{
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  mb: 0.5,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
            {/* Scoring gave up (review B-04). Its own badge rather than a
                capture-quality status, because it says something different:
                quality describes how well the sensors recorded, this says the
                recording was never turned into scores and never will be
                without intervention. Carries the reason in a tooltip — the
                two observed causes (the object missing from S3, a recording
                too short for the filter) call for opposite responses. */}
            {isSession && event.processingFailed && (
              <Tooltip
                title={
                  event.processingError
                    ? `Not scored — ${event.processingError}`
                    : 'Not scored. Processing stopped after repeated failures.'
                }
              >
                <Chip
                  label="Not scored"
                  color="error"
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    mb: 0.5,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              </Tooltip>
            )}
            {/* Organizational context (RFC procedure-instance, Phase 1b) —
                the most-specific tag (location, else department) as a
                scannable chip. Full department · location · kind rides the
                tile tooltip. Only shows once the session has been tagged
                and the caller resolved the names. */}
            {isSession && (event.locationName || event.departmentName) && (
              <Chip
                label={event.locationName || event.departmentName}
                size="small"
                variant="outlined"
                color="info"
                sx={{
                  maxWidth: 96,
                  height: 18,
                  fontSize: '0.62rem',
                  mb: 0.5,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
            {/* Actions stacked vertically (Download → Attach →
                Enable/Disable → Play) so the per-event tile stays
                narrow instead of cramming icons across. Icons-only;
                each carries a tooltip. In actionMenu / contextMenu mode
                these move into the popup below instead. */}
            {!useActionMenu && !contextMenu && (
              <Stack spacing={0.5} alignItems="center">
                {onDownload !== undefined &&
                  !(hideSessionDownload && isSession) && (
                    <Tooltip
                      title={
                        isSession
                          ? 'Download session bundle (ZIP: NDJSON + video + audio)'
                          : 'Download baseline'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={disabled || busyId === event.id}
                          // Let the click bubble to the Stack's onClick so
                          // tapping the icon also triggers `onSelect`. For
                          // baselines the JSON-row dump still fires; the
                          // select handler early-returns on non-session.
                          onClick={() => onDownload(event)}
                          aria-label={
                            isSession
                              ? 'Download session bundle'
                              : 'Download baseline'
                          }
                          sx={{
                            color: isSession
                              ? 'primary.main'
                              : 'secondary.main',
                            border: '1px solid',
                            borderColor: isSession
                              ? 'primary.light'
                              : 'secondary.light',
                            borderRadius: '50%',
                            p: 0.5,
                          }}
                        >
                          {busyId === event.id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <DownloadIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                {onAttach !== undefined && isSession && (
                  <Tooltip title="Attach video or audio">
                    <IconButton
                      size="small"
                      // Attach IS still a distinct action from select —
                      // we don't want clicking the paperclip to ALSO
                      // pop the trace card open while the operator is
                      // mid-attachment. Keep the stopPropagation here.
                      onClick={(e) => {
                        e.stopPropagation();
                        onAttach(event);
                      }}
                      aria-label="Attach video or audio"
                      sx={{
                        color: 'text.secondary',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '50%',
                        p: 0.5,
                      }}
                    >
                      <AttachFileIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {isDeleted && onRestore !== undefined && (
                  <Tooltip title="Re-enable — show in lists and include in scores">
                    <IconButton
                      size="small"
                      disabled={busyId === event.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRestore(event);
                      }}
                      aria-label="Restore"
                      sx={{
                        color: 'success.main',
                        border: '1px solid',
                        borderColor: 'success.light',
                        borderRadius: '50%',
                        p: 0.5,
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {!isDeleted && onDisable !== undefined && (
                  <Tooltip title="Disable — hide from lists and exclude from scores">
                    <IconButton
                      size="small"
                      disabled={busyId === event.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDisable(event);
                      }}
                      aria-label="Disable"
                      sx={{
                        color: 'error.main',
                        border: '1px solid',
                        borderColor: 'error.light',
                        borderRadius: '50%',
                        p: 0.5,
                      }}
                    >
                      <VisibilityOffIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {onReplay !== undefined && isSession && !isDeleted && (
                  <Tooltip title="Replay session">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onReplay(event);
                      }}
                      aria-label="Replay session"
                      sx={{
                        color: 'info.main',
                        border: '1px solid',
                        borderColor: 'info.light',
                        borderRadius: '50%',
                        p: 0.5,
                      }}
                    >
                      <PlayCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Tooltip>
  );
}
